import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './RegistrationModal.css';
import './Modal.css';
import './ErrorMessages.css';
import { supabase } from '../supabaseClient';
import DOMPurify from 'dompurify';

const timeSlotMap = {
  '9:30-12:00': '9:30 AM-12:00 PM (Cooking)',
  '12:00-2:30': '12:00 PM-2:30 PM (Serving)',
};

const RegistrationModal = ({ onClose, selectedProject }) => {
  // --- STATE MANAGEMENT ---
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('Lowell Transition Centre');
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('9:30-12:00');
  const [errors, setErrors] = useState({});
  const [isFull, setIsFull] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const modalRef = useRef(null);

  // --- CONSTANTS ---
  const isFeedTheNeedy = selectedProject?.name === 'Feed The Needy';
  const isDonationDrive = selectedProject?.name === 'Quarterly Donation Drive';
  const isDateProject = isFeedTheNeedy;
  const REGISTRATION_LIMIT = 5;

  // --- ACCESSIBILITY: Focus Trap & Escape Key ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Handle initial focus only once on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (modalRef.current) {
        // Focus the first empty or sensible element
        const firstElem = modalRef.current.querySelector('input:not([readonly]), select, button:not(.modal-close-button)');
        if (firstElem) firstElem.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // --- DATE CALCULATION LOGIC ---
  const getSundays = (count = 10) => {
    const dates = [];
    let date = new Date();
    date.setUTCHours(12, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() + (7 - date.getUTCDay()) % 7);
    for (let i = 0; i < count; i++) {
      dates.push(new Date(date));
      date.setUTCDate(date.getUTCDate() + 7);
    }
    return dates;
  };

  const getSecondSundays = (sundays) => {
    return sundays.filter(d => d.getUTCDate() >= 8 && d.getUTCDate() <= 14);
  };

  const getRegistrationCount = useCallback(async (date, location) => {
    if (!isFeedTheNeedy || !date) return;

    try {
      const { count, error } = await supabase
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .eq('program_name', 'Feed The Needy')
        .eq('program_date', date)
        .eq('location', location);

      if (error) {
        throw error;
      }

      setIsFull(count >= REGISTRATION_LIMIT);

    } catch (error) {
      console.error('Error fetching registration count:', error);
    }
  }, [isFeedTheNeedy]);

  // --- EFFECTS ---
  useEffect(() => {
    let datesToShow = [];
    if (isFeedTheNeedy) {
      const allSundays = getSundays(52);
      if (selectedLocation === 'Lowell Transition Centre') {
        datesToShow = allSundays.slice(0, 5);
      } else if (selectedLocation === 'Nashua Soup Kitchen') {
        datesToShow = getSecondSundays(allSundays).slice(0, 5);
      }
    }

    const now = new Date();
    const filteredDates = datesToShow.filter(date => {
      // Calculate the Thursday before the event date
      // Sunday (0) -> Thursday (-3)
      // Saturday (6) -> Thursday (-2)
      const eventDay = date.getUTCDay();
      const daysToSubtract = eventDay === 0 ? 3 : 2; // Sunday is 0, Saturday is 6

      const cutoff = new Date(date);
      cutoff.setUTCDate(cutoff.getUTCDate() - daysToSubtract);
      // 11 PM EST is 03:00 UTC (Next Day)
      // Since 11 PM Thursday is technically 3 AM Friday UTC, we adjust the time.
      cutoff.setUTCHours(3, 0, 0, 0); 
      // If we are setting it to 3 AM Friday, we need to add 1 day to our Thursday base
      cutoff.setUTCDate(cutoff.getUTCDate() + 1); 
      
      return now < cutoff;
    });

    setAvailableDates(filteredDates);

    if (filteredDates.length > 0) {
      const initialDate = filteredDates[0].toISOString().split('T')[0];
      setSelectedDate(initialDate);
      if (isFeedTheNeedy) {
        getRegistrationCount(initialDate, selectedLocation);
      }
    } else {
      setSelectedDate('');
      setIsFull(false);
    }
  }, [selectedProject, selectedLocation, isFeedTheNeedy, getRegistrationCount]);

  useEffect(() => {
    if (isFeedTheNeedy && selectedDate && selectedLocation) {
      getRegistrationCount(selectedDate, selectedLocation);
    }
  }, [selectedDate, selectedLocation, isFeedTheNeedy, getRegistrationCount]);


  useEffect(() => {
    if (isFeedTheNeedy && selectedLocation === 'Nashua Soup Kitchen') {
      setSelectedTimeSlot('9:30-12:00');
    } else {
      setSelectedTimeSlot('');
    }
  }, [isFeedTheNeedy, selectedLocation]);

  // --- DERIVED VALUES ---
  let timeValue = '';
  if (isFeedTheNeedy) {
    if (selectedLocation === 'Nashua Soup Kitchen') {
      timeValue = selectedTimeSlot;
    } else {
      timeValue = '10:30 am EST';
    }
  }

  // --- VALIDATION ---
  const validateForm = (data) => {
    const newErrors = {};
    if (!data.name.trim()) {
      newErrors.name = 'Name is required';
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(data.email)) {
      newErrors.email = 'Invalid email format';
    }
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(data.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRetry = () => {
    setSubmissionError(null);
    setIsSubmitted(false);
  };

  // --- HANDLERS ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isFeedTheNeedy && isFull) {
      setSubmissionError("This event is full. Please select a different date.");
      return;
    }

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    if (!validateForm(data)) {
      return;
    }

    const sanitizedName = DOMPurify.sanitize(data.name);
    const rawPhone = data.phone.replace(/\D/g, '');

    const locationAddressMap = {
      'Lowell Transition Centre': '193 Middlesex St, Lowell, MA',
      'Nashua Soup Kitchen': '2 Quincy St, Nashua, NH',
      'Om Hindu Community Center': '240 Boston Rd, North Billerica, MA',
    };

    let friendlyLocation = null;
    if (isFeedTheNeedy) {
      friendlyLocation = selectedLocation;
    } else if (isDonationDrive) {
      friendlyLocation = 'Town Hall centre';
    }

    const locationForDB = locationAddressMap[friendlyLocation] || friendlyLocation;

    // Final check before submission
    if (isFeedTheNeedy) {
      const { count } = await supabase
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .eq('program_name', 'Feed The Needy')
        .eq('program_date', selectedDate)
        .eq('location', locationForDB);

      if (count >= REGISTRATION_LIMIT) {
        setSubmissionError("Sorry, all spots for this event were just filled. Please select a different date.");
        setIsFull(true);
        return;
      }
    }


    const submissionData = {
      name: sanitizedName,
      phone_number: rawPhone,
      email: data.email,
      program_name: selectedProject.name,
      location: locationForDB,
      program_date: isDateProject ? selectedDate : null,
      time: isDateProject ? timeValue : null,
    };

    console.log('--- Attempting Supabase Insert ---');
    console.log('Submission Data:', submissionData);

    const { data: insertResult, error } = await supabase.from('registrations').insert([submissionData]);

    if (error) {
      console.error('SUPABASE INSERT ERROR:', error);
      setSubmissionError("We're sorry, but there was an error processing your registration. Please try again later.");
      setErrors({});
    } else {
      console.log('Insert Successful:', insertResult);
      setIsSubmitted(true);
      setSubmissionError(null);
    }
  };

  const handlePhoneInput = (e) => {
    const rawPhone = e.target.value.replace(/\D/g, '');
    let formattedPhone = rawPhone.substring(0, 10);
    if (formattedPhone.length > 6) {
      formattedPhone = `${formattedPhone.slice(0, 3)}-${formattedPhone.slice(3, 6)}-${formattedPhone.slice(6)}`;
    } else if (formattedPhone.length > 3) {
      formattedPhone = `${formattedPhone.slice(0, 3)}-${formattedPhone.slice(3)}`;
    }
    e.target.value = formattedPhone;
  };

  const handleNameInput = (e) => {
    e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    if (isFeedTheNeedy) {
      getRegistrationCount(newDate, selectedLocation);
    }
  };


  const formatDateForDisplay = (dateString) => {
    return new Date(dateString + 'T12:00:00Z').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  // --- RENDER ---
  const title = selectedProject ? `Register for ${selectedProject.name}` : 'Register for Volunteer Activity';

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-labelledby="registration-modal-title">
      <div className="modal-content" ref={modalRef}>
        <button className="modal-close-button" onClick={onClose} aria-label="Close dialog">
          &times;
        </button>
        <h2 id="registration-modal-title">{title}</h2>

        <div aria-live="polite">
          {submissionError ? (
            <div className="submission-feedback error">
              <div className="error-mark">&#x2717;</div>
              <p>{submissionError}</p>
              <button onClick={handleRetry} className="retry-button">Try Again</button>
            </div>
          ) : isSubmitted ? (
            <div className="submission-feedback">
              <div className="tick-mark">✓</div>
              <p>Submitted successfully!</p>
              {isFeedTheNeedy &&
                (selectedLocation === 'Nashua Soup Kitchen' ? (
                  <p>
                    Thank you for registering to volunteer at the{' '}
                    <strong>Nashua Soup Kitchen</strong>. You are scheduled for the{' '}
                    <strong>{timeSlotMap[timeValue]}</strong> slot on{' '}
                    <strong>{formatDateForDisplay(selectedDate)}</strong> to assist with{' '}
                    {timeValue === '9:30-12:00' ? 'cooking' : 'serving'}. Additional
                    instructions will be sent to you.
                  </p>
                ) : (
                  <p>
                    As a volunteer for the Feed the Needy initiative at{' '}
                    <strong>{selectedLocation}</strong>, you will be responsible for
                    delivering food on{' '}
                    <strong>{formatDateForDisplay(selectedDate)}</strong> at{' '}
                    <strong>{timeValue}</strong>. Additional instructions will be sent to
                    you.
                  </p>
                ))}

              {isDonationDrive && (
                <p>
                  Thank you for registering for the Quarterly Donation Drive. Please drop off your donations at <strong>Town Hall centre</strong>.
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {errors.form && <p className="error-message">{errors.form}</p>}
              <div className="form-group">
                <label htmlFor="reg-name">Name:</label>
                <input type="text" id="reg-name" name="name" required onInput={handleNameInput} />
                {errors.name && <p className="error-message">{errors.name}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="reg-phone">Phone Number:</label>
                <input type="tel" id="reg-phone" name="phone" required onInput={handlePhoneInput} pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}" title="Format: xxx-xxx-xxxx" />
                {errors.phone && <p className="error-message">{errors.phone}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="reg-email">Email:</label>
                <input type="email" id="reg-email" name="email" required />
                {errors.email && <p className="error-message">{errors.email}</p>}
              </div>

              {isFeedTheNeedy && (
                <div className="form-group">
                  <label htmlFor="reg-location">Location:</label>
                  <select
                    id="reg-location"
                    name="location"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    required
                  >
                    <option value="Lowell Transition Centre">Lowell Transition Centre</option>
                    <option value="Nashua Soup Kitchen">Nashua Soup Kitchen</option>
                  </select>
                </div>
              )}

              {isDonationDrive && (
                <div className="form-group">
                  <label htmlFor="reg-location-static">Location:</label>
                  <input
                    type="text"
                    id="reg-location-static"
                    name="location"
                    value="Town Hall centre"
                    readOnly
                  />
                </div>
              )}

              {isDateProject && (
                <>
                  <div className="form-group">
                    <label htmlFor="reg-date">Date:</label>
                    <select
                      id="reg-date"
                      name="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      required
                      disabled={availableDates.length === 0}
                    >
                      {availableDates.length > 0 ? (
                        availableDates.map((date) => (
                          <option key={date.toISOString()} value={date.toISOString().split('T')[0]}>
                            {date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                          </option>
                        ))
                      ) : (
                        <option value="">No available dates</option>
                      )}
                    </select>
                    {isFeedTheNeedy && isFull && <p className="error-message">This date is full. Please select another date.</p>}
                  </div>
                  {isFeedTheNeedy && selectedLocation === 'Nashua Soup Kitchen' ? (
                    <div className="form-group">
                      <label htmlFor="reg-time-slot">Time Slot:</label>
                      <select
                        id="reg-time-slot"
                        name="time-slot"
                        value={selectedTimeSlot}
                        onChange={(e) => setSelectedTimeSlot(e.target.value)}
                        required
                      >
                        <option value="9:30 AM-12:00 PM">9:30 AM-12:00 PM (Cooking)</option>
                        <option value="12:00 PM-2:30 PM">12:00 PM-2:30 PM (Serving)</option>
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="reg-time">Time:</label>
                      <input type="text" id="reg-time" name="time" value={timeValue} readOnly />
                    </div>
                  )}
                </>
              )}

              <div className="registration-form-footer">
                <div className="terms-checkbox-container">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={isAgreed}
                    onChange={(e) => setIsAgreed(e.target.checked)}
                    className="terms-checkbox"
                  />
                  <label htmlFor="terms-checkbox" className="terms-label">
                    I agree to the <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link>
                  </label>
                </div>
                <button 
                  type="submit" 
                  disabled={(isDateProject && availableDates.length === 0) || (isFeedTheNeedy && isFull) || !isAgreed}
                >
                  Submit
                </button>
                <div className="trust-signals">
                  <p style={{ fontSize: '0.8rem', fontStyle: 'italic', marginTop: '8px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Your registration helps us plan better. Thank you!
                  </p>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;
